<template>
	<div class="notfound-layout">
		<!-- Left Side - Zé Pilintra Image -->
		<div class="notfound-branding">
			<div class="background-overlay"></div>
		</div>

		<!-- Right Side - 404 Content -->
		<div class="notfound-content">
			<div class="notfound-container">
				<div class="notfound-header">
					<h1 class="notfound-code">404</h1>
					<p class="notfound-message">
						{{ $t("notFound.message") }}
					</p>
				</div>

				<v-btn
					color="primary"
					size="large"
					rounded="lg"
					class="notfound-btn"
					:to="{ name: 'Dashboard' }"
				>
					<v-icon start>mdi-home</v-icon>
					{{ $t("notFound.goHome") }} ({{ countdown }}s)
				</v-btn>
			</div>
		</div>
	</div>
</template>

<script setup lang="ts">
import { ref, onMounted, onUnmounted } from "vue";
import { useRouter } from "vue-router";

const router = useRouter();
const countdown = ref(5);
let timer: ReturnType<typeof setInterval> | null = null;

onMounted(() => {
	timer = setInterval(() => {
		countdown.value--;
		if (countdown.value <= 0) {
			if (timer) clearInterval(timer);
			router.push({ name: "Dashboard" });
		}
	}, 1000);
});

onUnmounted(() => {
	if (timer) clearInterval(timer);
});
</script>

<style lang="scss" scoped>
@use "@/styles/_variables.scss" as vars;
@use "@/styles/_theme-utilities.scss" as theme;

.notfound-layout {
	position: fixed;
	top: 0;
	left: 0;
	width: 100vw;
	height: 100vh;
	display: grid;
	grid-template-columns: 1fr 1fr;
	font-family: vars.$font-primary;
	margin: 0;
	padding: 0;
	overflow: hidden;

	@media (max-width: 968px) {
		grid-template-columns: 1fr;
		.notfound-branding {
			display: none;
		}
	}
}

.notfound-branding {
	@include theme.primary-gradient;
	display: flex;
	align-items: center;
	justify-content: center;
	position: relative;
	overflow: hidden;

	&::before {
		content: "";
		position: absolute;
		top: 0;
		left: 0;
		right: 0;
		bottom: 0;
		background: url('data:image/svg+xml,<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 100 100"><defs><pattern id="grain" width="100" height="100" patternUnits="userSpaceOnUse"><circle cx="20" cy="20" r="1" fill="%23ffffff" opacity="0.1"/><circle cx="80" cy="40" r="1" fill="%23ffffff" opacity="0.1"/><circle cx="40" cy="80" r="1" fill="%23ffffff" opacity="0.1"/></pattern></defs><rect width="100" height="100" fill="url(%23grain)"/></svg>');
		opacity: 0.3;
		z-index: 0;
	}

	.background-overlay {
		position: absolute;
		top: 0;
		left: 0;
		right: 0;
		bottom: 0;
		background-image: url("@/assets/images/backgrounds/zepilintra.jpg");
		background-size: auto 100%;
		background-position: center;
		background-repeat: no-repeat;
		opacity: 0.35;
		z-index: 1;
		filter: blur(1px);
	}
}

.notfound-content {
	display: flex;
	align-items: center;
	justify-content: center;
	padding: 2rem;
	background: rgb(var(--v-theme-surface));
	position: relative;

	&::before {
		content: "";
		position: absolute;
		top: 0;
		left: 0;
		right: 0;
		bottom: 0;
		background: linear-gradient(
			135deg,
			rgba(var(--v-theme-primary), 0.02) 0%,
			rgba(var(--v-theme-surface), 1) 50%,
			rgba(var(--v-theme-primary), 0.02) 100%
		);
		z-index: 0;
	}

	@media (max-width: 968px) {
		@include theme.primary-gradient;

		&::before {
			display: none;
		}

		.notfound-container {
			color: white;

			.notfound-code {
				color: white;
			}

			.notfound-message {
				color: rgba(255, 255, 255, 0.9);
			}
		}
	}
}

.notfound-container {
	text-align: center;
	position: relative;
	z-index: 1;
	max-width: 440px;
}

.notfound-header {
	margin-bottom: 2.5rem;

	.notfound-code {
		font-size: 8rem;
		font-weight: 800;
		color: rgb(var(--v-theme-primary));
		line-height: 1;
		margin-bottom: 1rem;
		font-family: vars.$font-heading;
		letter-spacing: -4px;
	}

	.notfound-message {
		font-size: 1.4rem;
		color: rgb(var(--v-theme-on-surface));
		opacity: 0.8;
		line-height: 1.5;
	}
}

.notfound-btn {
	@include theme.theme-button;
	height: 52px;
	font-size: 1.05rem;
	font-weight: 600;
	text-transform: none;
	border-radius: 16px;
	letter-spacing: 0.5px;
	padding: 0 2rem;
}
</style>
